import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import logo from '../assets/logo.png';
const API = process.env.REACT_APP_API_URL;

function HistoryPage() {
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(null); // invoice detail modal
    const [deleting, setDeleting] = useState(null);

    // ── Fetch all invoices ──
    const fetchInvoices = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const res = await axios.get(`${API}/invoices`)
            // newest first
            const sorted = res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setInvoices(sorted);
            setFiltered(sorted);
        } catch {
            setError('Could not load invoices. Make sure backend is running.');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

    // ── Search filter ──
    useEffect(() => {
        const q = search.toLowerCase().trim();
        if (!q) { setFiltered(invoices); return; }
        setFiltered(invoices.filter(inv =>
            (inv.customer_name || '').toLowerCase().includes(q) ||
            (inv.invoice_number || '').toLowerCase().includes(q) ||
            (inv.customer_address || '').toLowerCase().includes(q)
        ));
    }, [search, invoices]);

    // ── View detail ──
    const handleView = async (inv) => {
        try {
            const res = await axios.get(`${API}/invoices/${inv.id}`);
            setSelected(res.data);
        } catch { alert('Could not load invoice details.'); }
    };

    // ── Reopen (load into localStorage → go to invoice page) ──
    const handleReopen = async (inv) => {
        try {
            const res = await axios.get(`${API}/invoices/${inv.id}`);
            const data = res.data;

            // Rebuild rows
            const rows = (data.items || []).map(item => ({
                id: Date.now() + Math.random(),
                description: item.description || '',
                length: item.length || '',
                height: item.height || '',
                sqft: item.sqft || '',
                rate: item.rate || '',
                total_rate: item.total_rate || ''
            }));

            // Rebuild checklist
            const checklist = {
                invoice_number: data.invoice_number || '',
                invoice_date: data.invoice_date ? data.invoice_date.split('T')[0] : '',
                customer_name: data.customer_name || '',
                customer_address: data.customer_address || '',
                customer_phone: data.customer_phone || '',
                has_gst: !!data.has_gst,
                customer_gst: data.customer_gst || ''
            };

            localStorage.setItem('invoice_rows', JSON.stringify(rows));
            localStorage.setItem('invoice_checklist', JSON.stringify(checklist));
            localStorage.setItem('invoice_notes', data.notes || '');

            if (window.confirm(`"${data.customer_name}" estimate load ஆகுது. Invoice page-க்கு போகணுமா?`)) {
                navigate('/invoice');
            }
        } catch { alert('Could not reopen invoice.'); }
    };

    // ── Delete ──
    const handleDelete = async (inv) => {
        if (!window.confirm(`"${inv.customer_name}" (${inv.invoice_number}) estimate delete பண்றீங்களா?\n\nThis cannot be undone!`)) return;
        setDeleting(inv.id);
        try {
            await axios.delete(`${API}/invoices/${inv.id}`);
            setInvoices(prev => prev.filter(i => i.id !== inv.id));
            if (selected && selected.id === inv.id) setSelected(null);
        } catch { alert('Delete failed.'); }
        finally { setDeleting(null); }
    };

    // ── Format helpers ──
    const fDate = (s) => {
        if (!s) return '';
        const d = new Date(s);
        return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    };

    const fAmt = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

    // ── STATUS badge color by amount ──
    const amtColor = (n) => parseFloat(n) > 100000 ? '#7b1fa2' : parseFloat(n) > 50000 ? '#1565c0' : '#2e7d32';

    return (
        <div style={{ minHeight: '100vh', background: '#f5f5f5', fontFamily: 'Arial,sans-serif' }}>

            {/* ── HEADER ── */}
            <div style={{ background: 'linear-gradient(135deg,#ff6b00,#ff8c00)', boxShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>
                <div style={{ height: '5px', background: '#c43e00' }} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div style={{ background: 'white', borderRadius: '8px', padding: '5px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                            <img src={logo} alt="Logo" style={{ height: '55px', width: '55px', objectFit: 'contain', display: 'block' }} onError={e => e.target.style.display = 'none'} />
                        </div>
                        <div>
                            <div style={{ color: 'white', fontSize: '22px', fontWeight: '800', letterSpacing: '1px' }}>AKSHAYA</div>
                            <div style={{ color: 'rgba(255,255,255,0.92)', fontSize: '13px' }}>Wood Furniture & Interiors</div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ textAlign: 'right', color: 'white', fontSize: '12px', lineHeight: '1.8' }}>
                            <div style={{ fontWeight: '700' }}>Invoice History</div>
                            <div style={{ opacity: 0.85 }}>Total: {invoices.length} estimates</div>
                        </div>
                        <button onClick={() => navigate('/')} style={{
                            background: 'rgba(255,255,255,0.2)', color: 'white',
                            border: '2px solid rgba(255,255,255,0.7)', borderRadius: '8px',
                            padding: '8px 18px', cursor: 'pointer', fontSize: '13px', fontWeight: '700'
                        }}>
                            + New Estimate
                        </button>
                    </div>
                </div>
                <div style={{ height: '5px', background: '#c43e00' }} />
            </div>

            <div style={{ maxWidth: '1100px', margin: '28px auto', padding: '0 18px' }}>

                {/* ── SEARCH BAR ── */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '18px 22px', boxShadow: '0 2px 15px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '220px', position: 'relative' }}>
                            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px' }}>🔍</span>
                            <input
                                type="text"
                                placeholder="Search by customer name, estimate number, address..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                style={{
                                    width: '100%', padding: '10px 14px 10px 38px',
                                    border: '1.5px solid #ddd', borderRadius: '8px',
                                    fontSize: '14px', outline: 'none', boxSizing: 'border-box'
                                }}
                                onFocus={e => e.target.style.borderColor = '#ff6b00'}
                                onBlur={e => e.target.style.borderColor = '#ddd'}
                            />
                        </div>
                        {search && (
                            <button onClick={() => setSearch('')} style={{ background: '#607d8b', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 18px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                                Clear
                            </button>
                        )}
                        <button onClick={fetchInvoices} style={{ background: '#ff6b00', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 18px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                            🔄 Refresh
                        </button>
                    </div>
                    {search && (
                        <div style={{ marginTop: '8px', fontSize: '13px', color: '#888' }}>
                            {filtered.length} result{filtered.length !== 1 ? 's' : ''} found for "{search}"
                        </div>
                    )}
                </div>

                {/* ── LOADING / ERROR ── */}
                {loading && (
                    <div style={{ textAlign: 'center', padding: '60px 0', color: '#888', fontSize: '16px' }}>
                        Loading invoices...
                    </div>
                )}
                {error && (
                    <div style={{ background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: '10px', padding: '16px 20px', marginBottom: '16px', color: '#c62828', fontSize: '14px' }}>
                        ⚠️ {error}
                    </div>
                )}

                {/* ── EMPTY STATE ── */}
                {!loading && !error && filtered.length === 0 && (
                    <div style={{ background: 'white', borderRadius: '12px', padding: '60px 20px', textAlign: 'center', boxShadow: '0 2px 15px rgba(0,0,0,0.08)' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#555', marginBottom: '8px' }}>
                            {search ? 'No results found' : 'No invoices yet'}
                        </div>
                        <div style={{ fontSize: '14px', color: '#aaa', marginBottom: '24px' }}>
                            {search ? `"${search}" — try a different search term` : 'Create your first estimate to see it here'}
                        </div>
                        {!search && (
                            <button onClick={() => navigate('/')} style={{ background: '#ff6b00', color: 'white', border: 'none', borderRadius: '8px', padding: '12px 28px', cursor: 'pointer', fontSize: '15px', fontWeight: '600' }}>
                                + Create Estimate
                            </button>
                        )}
                    </div>
                )}

                {/* ── INVOICE LIST ── */}
                {!loading && filtered.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {filtered.map((inv, idx) => (
                            <div key={inv.id} style={{
                                background: 'white', borderRadius: '12px',
                                boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                                border: '1px solid #f0f0f0',
                                overflow: 'hidden',
                                transition: 'box-shadow 0.2s'
                            }}
                                onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.12)'}
                                onMouseOut={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.07)'}
                            >
                                {/* Card top strip */}
                                <div style={{ height: '4px', background: '#ff6b00' }} />

                                <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>

                                    {/* LEFT: info */}
                                    <div style={{ flex: 1, minWidth: '200px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px', flexWrap: 'wrap' }}>
                                            {/* Serial */}
                                            <span style={{ background: '#ff6b00', color: 'white', borderRadius: '6px', padding: '2px 9px', fontSize: '12px', fontWeight: '700' }}>
                                                #{invoices.length - invoices.findIndex(i => i.id === inv.id)}
                                            </span>
                                            <span style={{ fontSize: '15px', fontWeight: '700', color: '#222' }}>{inv.customer_name}</span>
                                            {inv.has_gst && (
                                                <span style={{ background: '#e8f5e9', color: '#2e7d32', borderRadius: '5px', padding: '2px 8px', fontSize: '11px', fontWeight: '600' }}>GST</span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#888', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                            <span>📄 {inv.invoice_number}</span>
                                            <span>📅 {fDate(inv.invoice_date || inv.created_at)}</span>
                                            {inv.customer_address && <span>📍 {inv.customer_address.substring(0, 30)}{inv.customer_address.length > 30 ? '...' : ''}</span>}
                                        </div>
                                    </div>

                                    {/* CENTER: amount */}
                                    <div style={{ textAlign: 'center', minWidth: '130px' }}>
                                        <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '3px' }}>NET AMOUNT</div>
                                        <div style={{ fontSize: '18px', fontWeight: '800', color: amtColor(inv.net_amount) }}>
                                            {fAmt(inv.net_amount)}
                                        </div>
                                        {inv.has_gst && (
                                            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                                                Subtotal: {fAmt(inv.subtotal)}
                                            </div>
                                        )}
                                    </div>

                                    {/* RIGHT: action buttons */}
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                        {/* View */}
                                        <button onClick={() => handleView(inv)} style={{
                                            background: '#fff3e0', color: '#e65100', border: '1.5px solid #ffcc80',
                                            borderRadius: '7px', padding: '8px 14px', cursor: 'pointer',
                                            fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px'
                                        }}>
                                            👁 View
                                        </button>
                                        {/* Reopen */}
                                        <button onClick={() => handleReopen(inv)} style={{
                                            background: '#e3f2fd', color: '#1565c0', border: '1.5px solid #90caf9',
                                            borderRadius: '7px', padding: '8px 14px', cursor: 'pointer',
                                            fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px'
                                        }}>
                                            🔄 Reopen
                                        </button>
                                        {/* Delete */}
                                        <button onClick={() => handleDelete(inv)} disabled={deleting === inv.id} style={{
                                            background: deleting === inv.id ? '#eee' : '#ffebee',
                                            color: deleting === inv.id ? '#aaa' : '#c62828',
                                            border: '1.5px solid #ef9a9a',
                                            borderRadius: '7px', padding: '8px 14px', cursor: deleting === inv.id ? 'default' : 'pointer',
                                            fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px'
                                        }}>
                                            {deleting === inv.id ? '...' : '🗑 Delete'}
                                        </button>
                                    </div>

                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Summary footer */}
                {!loading && invoices.length > 0 && (
                    <div style={{ marginTop: '24px', background: 'white', borderRadius: '12px', padding: '16px 22px', boxShadow: '0 2px 10px rgba(0,0,0,0.07)', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                        <div style={{ fontSize: '14px', color: '#555' }}>
                            <strong>Total Estimates:</strong> {invoices.length}
                        </div>
                        <div style={{ fontSize: '14px', color: '#555' }}>
                            <strong>Total Revenue:</strong>{' '}
                            <span style={{ color: '#ff6b00', fontWeight: '700' }}>
                                {fAmt(invoices.reduce((s, i) => s + (parseFloat(i.net_amount) || 0), 0))}
                            </span>
                        </div>
                        <div style={{ fontSize: '14px', color: '#555' }}>
                            <strong>Showing:</strong> {filtered.length} of {invoices.length}
                        </div>
                    </div>
                )}
            </div>

            {/* ── VIEW MODAL ── */}
            {selected && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.55)', zIndex: 1000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
                }} onClick={() => setSelected(null)}>
                    <div style={{
                        background: 'white', borderRadius: '14px', maxWidth: '720px', width: '100%',
                        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                    }} onClick={e => e.stopPropagation()}>

                        {/* Modal header */}
                        <div style={{ background: '#ff6b00', color: 'white', padding: '16px 22px', borderRadius: '14px 14px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <div style={{ fontSize: '18px', fontWeight: '800' }}>{selected.customer_name}</div>
                                <div style={{ fontSize: '13px', opacity: 0.9 }}>{selected.invoice_number} · {fDate(selected.invoice_date || selected.created_at)}</div>
                            </div>
                            <button onClick={() => setSelected(null)} style={{ background: 'rgba(255,255,255,0.25)', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '16px', fontWeight: '700' }}>✕</button>
                        </div>

                        <div style={{ padding: '22px' }}>
                            {/* Customer info */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                                <div style={{ background: '#fff8f3', borderRadius: '8px', padding: '14px', border: '1px solid #ffe0c0' }}>
                                    <div style={{ fontSize: '11px', color: '#ff6b00', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Customer</div>
                                    <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '4px' }}>{selected.customer_name}</div>
                                    <div style={{ color: '#555', fontSize: '13px', lineHeight: '1.6' }}>{selected.customer_address}</div>
                                    {selected.customer_phone && <div style={{ fontSize: '13px', marginTop: '4px' }}>📞 {selected.customer_phone}</div>}
                                    {selected.customer_gst && <div style={{ fontSize: '13px', marginTop: '4px' }}>GST: {selected.customer_gst}</div>}
                                </div>
                                <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '14px', border: '1px solid #e9ecef' }}>
                                    <div style={{ fontSize: '11px', color: '#666', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Invoice Details</div>
                                    <div style={{ fontSize: '13px', lineHeight: '2' }}>
                                        <div><strong>Estimate No:</strong> <span style={{ color: '#ff6b00', fontWeight: '700' }}>{selected.invoice_number}</span></div>
                                        <div><strong>Date:</strong> {fDate(selected.invoice_date || selected.created_at)}</div>
                                        <div><strong>GST:</strong> {selected.has_gst ? '18% Applied' : 'Not Applied'}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Items table */}
                            <div style={{ marginBottom: '16px', overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '480px' }}>
                                    <thead>
                                        <tr style={{ background: '#ff6b00', color: 'white' }}>
                                            {['S.No', 'Description', 'Length', 'Height', 'Sqft/Qty', 'Rate(₹)', 'Total(₹)'].map((h, i) => (
                                                <th key={i} style={{ padding: '9px 8px', border: '1px solid #e07000', textAlign: i === 1 ? 'left' : 'center' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(selected.items || []).map((item, i) => (
                                            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fff8f3' }}>
                                                <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>{item.sno}</td>
                                                <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: '500' }}>{item.description}</td>
                                                <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>{item.length || '-'}</td>
                                                <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>{item.height || '-'}</td>
                                                <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>{item.sqft || '-'}</td>
                                                <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{item.rate ? `₹${parseFloat(item.rate).toLocaleString('en-IN')}` : '-'}</td>
                                                <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right', fontWeight: '600' }}>{item.total_rate ? `₹${parseFloat(item.total_rate).toLocaleString('en-IN')}` : '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Totals */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                                <table style={{ borderCollapse: 'collapse', fontSize: '14px', minWidth: '240px' }}>
                                    <tbody>
                                        <tr><td style={{ padding: '6px 12px', border: '1px solid #ddd', textAlign: 'right', background: '#f9f9f9', fontWeight: '600' }}>Subtotal:</td><td style={{ padding: '6px 12px', border: '1px solid #ddd', textAlign: 'right' }}>{fAmt(selected.subtotal)}</td></tr>
                                        {selected.has_gst && <>
                                            <tr><td style={{ padding: '6px 12px', border: '1px solid #ddd', textAlign: 'right', background: '#f9f9f9' }}>SGST (9%):</td><td style={{ padding: '6px 12px', border: '1px solid #ddd', textAlign: 'right' }}>{fAmt(selected.sgst)}</td></tr>
                                            <tr><td style={{ padding: '6px 12px', border: '1px solid #ddd', textAlign: 'right', background: '#f9f9f9' }}>CGST (9%):</td><td style={{ padding: '6px 12px', border: '1px solid #ddd', textAlign: 'right' }}>{fAmt(selected.cgst)}</td></tr>
                                        </>}
                                        <tr>
                                            <td style={{ padding: '9px 12px', background: '#ff6b00', color: 'white', fontWeight: '800', textAlign: 'right', fontSize: '15px' }}>NET AMOUNT:</td>
                                            <td style={{ padding: '9px 12px', background: '#ff6b00', color: 'white', fontWeight: '800', textAlign: 'right', fontSize: '15px' }}>{fAmt(selected.net_amount)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Action buttons inside modal */}
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                <button onClick={() => { handleReopen(selected); setSelected(null); }} style={{
                                    background: '#1976d2', color: 'white', border: 'none', borderRadius: '8px',
                                    padding: '11px 22px', cursor: 'pointer', fontSize: '14px', fontWeight: '600'
                                }}>🔄 Reopen & Edit</button>
                                <button onClick={() => { handleDelete(selected); }} style={{
                                    background: '#c62828', color: 'white', border: 'none', borderRadius: '8px',
                                    padding: '11px 22px', cursor: 'pointer', fontSize: '14px', fontWeight: '600'
                                }}>🗑 Delete</button>
                                <button onClick={() => setSelected(null)} style={{
                                    background: '#607d8b', color: 'white', border: 'none', borderRadius: '8px',
                                    padding: '11px 22px', cursor: 'pointer', fontSize: '14px', fontWeight: '600'
                                }}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default HistoryPage;